/* Shared serialization keeps PDP additions and cart edits from racing each other. */
(() => {
  // Exact retired MTO identities from the approved manifest and verified created-ID journal.
  const legacyMto = {"43318711156914":["67663137833138","7807636308146"],"43318711189682":["67663137865906","7807636308146"],"45219542106290":["67663137931442","7807636308146"],"43318711287986":["67663137964210","7807636406450"],"43318711320754":["67663137996978","7807636406450"],"45219540369586":["67663138062514","7807636406450"],"43318711386290":["67663138095282","7807635357874"],"43318711419058":["67663138160818","7807635357874"],"45219539255474":["67663138193586","7807635357874"],"43318711517362":["67663138226354","7145447391410"],"43318711550130":["67663138291890","7145447391410"],"45219530834098":["67663138324658","7145447391410"],"43318827942066":["67663138422962","7807680282802"],"43318827974834":["67663138455730","7807680282802"],"45219466018994":["67663138488498","7807680282802"],"43318846521522":["67663138554034","7807687229618"],"43318846554290":["67663138586802","7807687229618"],"45219465232562":["67663138619570","7807687229618"],"43318847930546":["67664143745202","15415170629810"],"43318847897778":["67664143712434","15415170629810"],"43319683481778":["67663138685106","7807636111538"],"43319683514546":["67663138717874","7807636111538"],"45219460612274":["67663138783410","7807636111538"],"43319684006066":["67663138816178","7807891996850"],"43320431444146":["67663138848946","7807891996850"],"45219459825842":["67663138914482","7807891996850"],"43320513102002":["67663138947250","7807891898546"],"43320513134770":["67663138980018","7807891898546"],"45219459399858":["67663139012786","7807891898546"],"46754326380722":["67663139078322","7807891898546"],"46754326413490":["67663139111090","7807891898546"],"46754326446258":["67663139176626","7807891898546"],"43320524538034":["67663139209394","7807717540018"],"43320524570802":["67663139242162","7807717540018"],"45219459301554":["67663139307698","7807717540018"],"46754324971698":["67663139340466","7807717540018"],"46754325004466":["67663139406002","7807717540018"],"46754325037234":["67663139438770","7807717540018"],"47331709812914":["67663139471538","7868994420914"]};
  const promiseAttribute = '_wolf_bundle_promises_v1';
  const checkoutError = 'Your bag’s shipping details could not be saved. Please try checkout again.';
  const bundlePromises = (cart) => {
    if (!Array.isArray(cart?.items)) throw new Error(checkoutError);
    const parents = new Map();
    for (const item of cart.items) {
      if (!item.has_components && !item.item_components?.length && !item.bundle_components?.length) continue;
      const id = String(item.variant_id || item.id), quantity = item.quantity, properties = item.properties || {};
      if (!/^[1-9]\d*$/.test(id) || !Number.isSafeInteger(quantity) || quantity < 1) throw new Error(checkoutError);
      const raw = String(properties._wolf_mto_weeks || '');
      const text = String(properties['Shipping time'] || '');
      let promise = '';
      if (properties.Fulfillment === 'Made to Order') {
        const textWeeks = /^Up to ([1-9]\d*) weeks$/.exec(text)?.[1];
        const weeks = raw || textWeeks || '';
        if (!/^[1-9]\d*$/.test(weeks) || Number(weeks) > 52 || (raw && raw !== weeks) || (text && textWeeks !== weeks)) throw new Error(checkoutError);
        promise = weeks;
      } else if (properties.Fulfillment === 'Ready to Ship') {
        if (text !== '3–5 business days' || raw) throw new Error(checkoutError);
        promise = 'rts';
      }
      // Unrecognized older purchases remain neutral in the email; never infer timing.
      const previous = parents.get(id);
      if (previous && previous.promise !== promise) throw new Error(checkoutError);
      const total = quantity + (previous?.quantity || 0);
      if (!Number.isSafeInteger(total)) throw new Error(checkoutError);
      parents.set(id, { quantity: total, promise });
    }
    const tuples = [...parents].filter(([, value]) => value.promise).sort(([a], [b]) => a.length - b.length || a.localeCompare(b))
      .map(([id, value]) => `${id}:${value.quantity}:${value.promise}`);
    return tuples.length ? `v1|${tuples.join('|')}|` : '';
  };
  const replaceLine = async (cart, original, variant, quantity, properties = original.properties || {}, transport = {}) => {
    const root = window.Shopify?.routes?.root || '/';
    const readCart = transport.readCart || readFreshCart;
    const post = transport.post || postCart;
    const identity = window.WolfCartGuard.identity;
    const sourceIdentity = identity(original);
    const targetIdentity = identity({ ...original, properties }, variant.id);
    const matching = (state, signature) => state.items.filter((item) => identity(item) === signature);
    const total = (state, signature) => matching(state, signature).reduce((sum, item) => sum + item.quantity, 0);
    const sourceBefore = total(cart, sourceIdentity);
    const targetBefore = total(cart, targetIdentity);
    const targetExpected = targetBefore + quantity;
    const sourceLine = (state) => {
      const candidates = matching(state, sourceIdentity).filter((item) => item.quantity === original.quantity);
      return candidates.find((item) => item.key === original.key) || (candidates.length === 1 ? candidates[0] : null);
    };
    const rollback = async (state) => {
      // Only undo a confirmed added amount when the original line is intact and
      // the target is unambiguous. Never overwrite another tab's changed cart.
      const targets = matching(state, targetIdentity);
      const added = total(state, targetIdentity) - targetBefore;
      if (total(state, sourceIdentity) !== sourceBefore || !sourceLine(state) ||
          targets.length !== 1 || added <= 0 || added > quantity) return;
      try {
        await post(`${root}cart/change.js`, { id: targets[0].key, quantity: targetBefore });
      } catch (_) { /* The caller reconciles actual state, even after a lost response. */ }
    };

    // Shopify cannot swap a line's variant. Secure its replacement first, then
    // remove its old line; keep checkout locked throughout the shared queue.
    try {
      await post(`${root}cart/add.js`, { items: [{ id: variant.id, quantity, properties }] });
    } catch (_) { /* An add can partially succeed: inspect it once, never retry. */ }
    let actual = await readCart();
    const source = sourceLine(actual);
    if (total(actual, targetIdentity) !== targetExpected || total(actual, sourceIdentity) !== sourceBefore || !source) {
      await rollback(actual);
      throw new Error('Fulfillment replacement was not confirmed.');
    }
    try {
      await post(`${root}cart/change.js`, { id: source.key, quantity: 0 });
    } catch (_) { /* A lost response is safe to accept only after verifying the result. */ }
    actual = await readCart();
    if (total(actual, sourceIdentity) !== sourceBefore - original.quantity || total(actual, targetIdentity) !== targetExpected) {
      await rollback(actual);
      throw new Error('Fulfillment replacement was not confirmed.');
    }
    const targets = matching(actual, targetIdentity);
    return { cart: actual, key: targets[0]?.key };
  };

  const migrationError = 'We could not update a made-to-order item in your bag. Your original selection is saved. Please refresh your bag and try again, or contact us for help.';
  const migrationJournalKey = 'wolf-legacy-mto-cart-v1';
  const readFreshCart = async () => {
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
    if (!response.ok) throw new Error(migrationError);
    const cart = await response.json();
    if (!Array.isArray(cart.items)) throw new Error(migrationError);
    return cart;
  };
  const postCart = async (url, body) => {
    const response = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) });
    const result = await response.json();
    if (!response.ok || result.errors || result.status) throw new Error(migrationError);
    return result;
  };
  const migrationProperties = (original, result) => {
    const properties = { ...(original.properties || {}) };
    if (properties.Fulfillment && properties.Fulfillment !== 'Made to Order') throw new Error(migrationError);
    properties.Fulfillment ||= 'Made to Order';
    if (!properties['Shipping time']) {
      // Preserve every existing public promise. Only an absent promise gets today's rules.
      const savedWeeks = String(properties._wolf_mto_weeks || '');
      if (savedWeeks && (!/^[1-9]\d*$/.test(savedWeeks) || Number(savedWeeks) > 52)) throw new Error(migrationError);
      properties['Shipping time'] = savedWeeks ? `Up to ${savedWeeks} weeks` : result.lead;
    }
    if (!properties._wolf_mto_weeks) {
      const weeks = /^Up to ([1-9]\d*) weeks$/.exec(properties['Shipping time'])?.[1];
      if (weeks && Number(weeks) <= 52) properties._wolf_mto_weeks = weeks;
    }
    // An old, explicitly MTO selection must never become automatic RTS on a later edit.
    properties._wolf_fulfillment_routing = 'legacy-mto';
    return properties;
  };
  const migrateLegacyCart = async (initialCart) => {
    if (!Array.isArray(initialCart?.items)) throw new Error(migrationError);
    let cart = initialCart, changed = false;
    const hasLegacy = () => cart.items.some((item) => legacyMto[String(item.variant_id || item.id)]);
    let journal;
    try { journal = JSON.parse(window.sessionStorage?.getItem(migrationJournalKey) || 'null'); }
    catch (_) { if (hasLegacy()) throw new Error(migrationError); }
    if (!hasLegacy() && !journal) return cart;
    const storeJournal = (value) => {
      // Save recovery before a write. Storage-denied browsers retain their original bag.
      if (!window.sessionStorage) throw new Error(migrationError);
      if (value) window.sessionStorage.setItem(migrationJournalKey, JSON.stringify(value));
      else window.sessionStorage.removeItem(migrationJournalKey);
      journal = value;
    };
    const identity = window.WolfCartGuard.identity;
    const matches = (state, signature) => state.items.filter((item) => identity(item) === signature);
    const total = (state, signature) => matches(state, signature).reduce((sum, item) => sum + item.quantity, 0);
    const signatures = (record) => [identity(record.original), identity({ ...record.original, properties: record.properties }, record.targetId)];
    const recover = async () => {
      if (!journal) return;
      if (journal.cartToken !== cart.token) { storeJournal(null); return; }
      const approved = legacyMto[String(journal.original?.variant_id || journal.original?.id)];
      if (!approved || approved[0] !== String(journal.targetId)) throw new Error(migrationError);
      const [source, target] = signatures(journal);
      const sourceNow = total(cart, source), targetNow = total(cart, target);
      const complete = sourceNow === journal.sourceBefore - journal.original.quantity && targetNow === journal.targetBefore + journal.original.quantity;
      const untouched = sourceNow === journal.sourceBefore && targetNow === journal.targetBefore;
      if (complete || untouched) { changed ||= complete; storeJournal(null); return; }
      if (sourceNow === journal.sourceBefore && targetNow === journal.targetBefore + journal.original.quantity) {
        const sources = matches(cart, source).filter((item) => item.quantity === journal.original.quantity);
        const original = sources.find((item) => item.key === journal.original.key) || (sources.length === 1 ? sources[0] : null);
        if (original) {
          // The replacement already exists: a retry only finishes removal, never adds twice.
          try { await postCart(`${window.Shopify?.routes?.root || '/'}cart/change.js`, { id: original.key, quantity: 0 }); } catch (_) {}
          cart = await readFreshCart();
          if (total(cart, source) === journal.sourceBefore - journal.original.quantity && total(cart, target) === journal.targetBefore + journal.original.quantity) {
            changed = true; storeJournal(null); return;
          }
        }
      }
      throw new Error(migrationError);
    };
    await recover();
    if (hasLegacy()) {
      if (!window.WolfPurchasing) throw new Error(migrationError);
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart?section_id=wolf-cart-migrations`, { cache: 'no-store' });
      if (!response.ok) throw new Error(migrationError);
      const markup = new DOMParser().parseFromString(await response.text(), 'text/html');
      let configs;
      try { configs = JSON.parse(markup.querySelector('[data-wolf-cart-migration-configs]')?.textContent || 'null'); } catch (_) {}
      if (!configs) throw new Error(migrationError);
      // Re-read after Liquid so another tab's quantity or properties are never overwritten.
      cart = await readFreshCart();
      for (const original of [...cart.items]) {
        const alias = legacyMto[String(original.variant_id || original.id)];
        if (!alias) continue;
        const config = configs[alias[1]];
        const target = config?.variants?.find((variant) => String(variant.id) === alias[0] && String(variant.productId) === alias[1] && variant.kind === 'mto');
        if (!target || !config.nativeFulfillment || original.selling_plan_allocation || !Number.isSafeInteger(original.quantity) || original.quantity < 1) throw new Error(migrationError);
        const result = window.WolfPurchasing.resolve(config, window.WolfPurchasing.physicalOptions(target), original.quantity,
          { ...cart, items: cart.items.filter((item) => item.key !== original.key) }, { keepMto: true });
        if (!result.purchasable || result.state !== 'mto' || String(result.variant.id) !== alias[0]) throw new Error(migrationError);
        const properties = migrationProperties(original, result);
        const source = identity(original), targetSignature = identity({ ...original, properties }, target.id);
        storeJournal({ cartToken: cart.token, original, targetId: target.id, properties, sourceBefore: total(cart, source), targetBefore: total(cart, targetSignature) });
        try {
          const converted = await replaceLine(cart, original, target, original.quantity, properties);
          cart = converted.cart;
          changed = true;
          storeJournal(null);
        } catch (_) {
          cart = await readFreshCart();
          // Clear only a fully rolled back attempt or a verified completed replacement.
          const complete = total(cart, source) === journal.sourceBefore - original.quantity && total(cart, targetSignature) === journal.targetBefore + original.quantity;
          const untouched = total(cart, source) === journal.sourceBefore && total(cart, targetSignature) === journal.targetBefore;
          if (complete || untouched) storeJournal(null);
          if (!complete) throw new Error(migrationError);
          changed = true;
        }
      }
    }
    if (changed && typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'wolf-legacy-migration', cartData: cart });
    }
    return cart;
  };

  const synchronizePromises = async () => {
    const root = window.Shopify?.routes?.root || '/';
    const read = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
    if (!read.ok) throw new Error(checkoutError);
    const cart = await migrateLegacyCart(await read.json()), value = bundlePromises(cart);
    if ((cart.attributes?.[promiseAttribute] || '') === value) return cart;
    const write = await fetch(`${root}cart/update.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: { [promiseAttribute]: value } }) });
    if (!write.ok) throw new Error(checkoutError);
    // The update response can flatten native bundles; verify the same cart.js shape used to build the tuple.
    const verify = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
    if (!verify.ok) throw new Error(checkoutError);
    const saved = await verify.json();
    if (bundlePromises(saved) !== value || (saved.attributes?.[promiseAttribute] || '') !== value) throw new Error(checkoutError);
    return saved;
  };
  if (!window.WolfCartMutation) {
    let tail = Promise.resolve();
    let pending = 0;
    const hasUnsavedQuantity = () => [...document.querySelectorAll('cart-items .quantity__input, cart-drawer-items .quantity__input')]
      .some((input) => input.value !== input.getAttribute('value'));
    const refreshCheckout = () => document.documentElement.toggleAttribute('data-wolf-cart-dirty', hasUnsavedQuantity());
    window.WolfCartMutation = {
      get pending() { return pending; },
      refreshCheckout,
      run(task, { requirePromise = false } = {}) {
        pending += 1;
        document.documentElement.toggleAttribute('data-wolf-cart-pending', true);
        document.documentElement.toggleAttribute('data-wolf-cart-promise-unverified', true);
        const result = tail.then(async () => {
          const result = await task();
          if (typeof fetch === 'function') {
            try {
              await synchronizePromises();
              document.documentElement.toggleAttribute('data-wolf-cart-promise-unverified', false);
            } catch (error) {
              // An added item must not look like a failed addition and invite a duplicate retry.
              if (requirePromise) throw error;
            }
          }
          return result;
        }).finally(() => {
          pending -= 1;
          document.documentElement.toggleAttribute('data-wolf-cart-pending', pending > 0);
          refreshCheckout();
        });
        tail = result.catch(() => {});
        return result;
      },
    };
    ['input', 'change'].forEach((type) => document.addEventListener(type, (event) => {
      if (event.target.matches?.('cart-items .quantity__input, cart-drawer-items .quantity__input')) refreshCheckout();
    }, true));
    const approvedForms = new WeakSet();
    document.addEventListener('submit', (event) => {
      if (event.submitter?.name !== 'checkout') return;
      if (approvedForms.delete(event.target)) return;
      event.preventDefault();
      if (pending > 0 || hasUnsavedQuantity()) return;
      const form = event.target, submitter = event.submitter;
      window.WolfCartMutation.run(async () => {}, { requirePromise: true }).then(() => {
        if (pending > 0 || hasUnsavedQuantity() || !form?.requestSubmit) return;
        approvedForms.add(form);
        form.requestSubmit(submitter);
      }).catch((failure) => {
        const message = failure.message === migrationError ? migrationError : checkoutError;
        const error = document.querySelector('#cart-errors, #CartDrawer-CartErrors');
        if (error) error.textContent = message;
        else window.alert?.(message);
      });
    }, true);
    if (typeof fetch === 'function') {
      document.documentElement.toggleAttribute('data-wolf-cart-promise-unverified', true);
      const initialize = () => window.WolfCartMutation.run(async () => {}).catch(() => {});
      if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
      else initialize();
    }
  }

  window.WolfCartGuard = {
    bundlePromises,
    synchronizePromises,
    replaceLine,
    migrateLegacyCart,
    legacyMto,
    identity(item, variantId = item.variant_id || item.id) {
      const properties = Object.entries(item.properties || {}).sort(([a], [b]) => a.localeCompare(b));
      return JSON.stringify([String(variantId), properties, String(item.selling_plan_allocation?.selling_plan?.id || '')]);
    },
    resolveIncrease(line, cart, quantity, { rtsRejected = false } = {}) {
      const current = cart.items.find((item) => item.key === line.key);
      if (!current || !Number.isSafeInteger(quantity) || quantity < 0) {
        return { action: 'error', message: 'Your bag changed. Please review it and try again.' };
      }
      // Only storefront automatic purchases may return to RTS. Registry and
      // historical explicit MTO lines deliberately have no automatic marker.
      const automaticMto = line.kind === 'mto' && line.purchasing?.nativeFulfillment &&
        current.properties?._wolf_fulfillment_routing === 'automatic' && !current.selling_plan_allocation;
      if (!line.guarded || quantity === 0 || (quantity <= current.quantity && !automaticMto)) return { action: 'change' };
      if (!line.purchasing || !window.WolfPurchasing) {
        if (line.kind === 'mto') return { action: 'error', message: 'Made to order availability could not be confirmed. Please refresh your bag.' };
        const message = this.validateIncrease(line, cart, quantity);
        return { action: message ? 'error' : 'change', message };
      }
      const source = line.purchasing.variants.find((variant) => String(variant.id) === String(current.variant_id || current.id));
      if (!source || !['rts', 'mto'].includes(source.kind) || String(source.id) !== String(line.variantId)) {
        return { action: 'error', message: 'This item’s availability could not be confirmed. Please refresh your bag.' };
      }
      // A Shopify 422 can reveal less sellable stock than Liquid reported. Keep
      // all permissions/rules, but exclude the rejected RTS route for this edit.
      const config = rtsRejected && source.kind === 'rts' ? { ...line.purchasing,
        variants: line.purchasing.variants.map((variant) => variant === source ? { ...variant, available: false } : variant),
      } : line.purchasing;
      const result = window.WolfPurchasing.resolve(config, window.WolfPurchasing.physicalOptions(source), quantity,
        { items: cart.items.filter((item) => item.key !== current.key) }, { keepMto: source.kind === 'mto' && !automaticMto });
      if (!result.purchasable) {
        // Removing some already-selected MTO units remains possible after an
        // offer closes; never invent a replacement when current rules reject it.
        if (quantity <= current.quantity) return { action: 'change' };
        return { action: 'error', message: window.WolfPurchasing.message(result) };
      }
      if (String(result.variant.id) !== String(source.id)) {
        // A linked product may have different subscription plans; never discard or guess a plan.
        if (current.selling_plan_allocation) return { action: 'error', message: 'Please review this subscription on the product page before changing fulfillment.' };
        return { action: 'convert', variant: result.variant,
          properties: config.nativeFulfillment ? window.WolfPurchasing.purchaseProperties(result, current.properties) : undefined };
      }
      const properties = source.kind === 'mto' && config.nativeFulfillment
        ? window.WolfPurchasing.purchaseProperties(result, current.properties) : undefined;
      if (properties && !automaticMto) {
        delete properties._wolf_fulfillment_routing;
        if (current.properties?._wolf_fulfillment_routing) properties._wolf_fulfillment_routing = current.properties._wolf_fulfillment_routing;
      }
      return { action: 'change', properties };
    },
    validateIncrease(line, cart, requestedQuantity) {
      const current = cart.items.find((item) => item.key === line.key);
      if (!current) return 'Your bag changed. Please review it and try again.';
      if (!Number.isSafeInteger(requestedQuantity) || requestedQuantity < 0) return 'Please enter a valid quantity.';
      // Reductions and removals never change the purchased fulfillment variant.
      if (requestedQuantity <= current.quantity || !line.guarded) return '';
      if (line.kind !== 'rts' || line.inventoryManagement !== 'shopify' || line.inventoryPolicy !== 'deny'
        || !Number.isSafeInteger(line.stock) || line.stock < 0 || !line.available) {
        return 'Ready to ship availability could not be confirmed. Please reduce your quantity or review this product.';
      }
      const otherUnits = cart.items.reduce((total, item) => (
        String(item.variant_id || item.id) === String(line.variantId) && item.key !== line.key
          ? total + item.quantity : total
      ), 0);
      const availableForLine = Math.max(0, line.stock - otherUnits);
      if (requestedQuantity <= availableForLine) return '';
      const availability = availableForLine > 0 && availableForLine <= 10
        ? `Only ${availableForLine} available for this item.`
        : 'This quantity isn’t available.';
      return `${availability} Please reduce your quantity or visit the product page to review made to order options.`;
    },
  };
})();
